import { AllContexts } from "../events/allEvents";
import { globalEventBus } from "../events/eventBus";
import { initEventBuffer, track as trackFn, TrackUser } from "../tracking/track";
import { invariant } from "ts-invariant";
import { TrackEvent } from "@common/schemas/track";
import { log } from "../log";

type GetUserFn = () => { user_id?: string; user_traits?: any; anonymous_id?: string };
type TrackFn = typeof trackFn;

export interface TourAnalyticsCollectorInitParams {
  appId: string;
  apiBaseUrl: string;
  track?: TrackFn;
  getUser?: GetUserFn;
  isPreview?: boolean;
  skipTracking?: boolean;
}

export class TourAnalyticsCollector {
  static init({
    appId,
    apiBaseUrl,
    track: _track,
    getUser,
    isPreview,
    skipTracking,
  }: TourAnalyticsCollectorInitParams) {
    initEventBuffer(apiBaseUrl, appId);
    let track = _track ?? trackFn;

    const safeTrack = (event: TrackEvent, user?: TrackUser) => {
      try {
        track(event, user);
      } catch (e) {
        log("Error tracking event", e);
      }
    };

    // We need to send preview event anyway
    if (isPreview) {
      globalEventBus.on(
        "tourSessionManager.tourSessionStarted",
        ({ context, payload: { reason, sessionId, startedAt } }) => {
          invariant(context.guide, "Guide is not defined");
          if (reason === "preview") {
            safeTrack(
              {
                event: "tour_preview_session_started",
                properties: {
                  guideId: context.guide.id,
                  guideName: context.guide.title ?? "Untitled",
                  tourSessionId: sessionId,
                  startedAt,
                },
              },
              getUser?.()
            );
          }
        }
      );

      return;
    }

    if (skipTracking) {
      return;
    }

    globalEventBus.on(
      "tourSessionManager.tourSessionStarted",
      ({ context, payload: { reason, sessionId, startedAt } }) => {
        invariant(context.guide, "Guide is not defined");

        safeTrack(
          {
            event: "tour_session_started",
            properties: {
              guideId: context.guide.id,
              guideName: context.guide.title ?? "Untitled",
              tourSessionId: sessionId,
              reason,
              startedAt,
            },
          },
          getUser?.()
        );
      }
    );

    globalEventBus.on("tourSessionManager.tourSessionResumed", ({ context, payload }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "tour_session_resumed",
          properties: {
            guideId: context.guide.id,
            guideName: context.guide.title ?? "Untitled",
            tourSessionId: payload.sessionId,
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.userEngaged", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "user_engaged",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourSessionManager.tourSessionExpired", ({ context, payload }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "tour_session_expired",
          properties: {
            guideId: context.guide.id,
            guideName: context.guide.title ?? "Untitled",
            tourSessionId: payload.sessionId,
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourSessionManager.tourSessionTerminated", ({ context, payload }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "tour_session_terminated",
          properties: {
            guideId: context.guide.id,
            guideName: context.guide.title ?? "Untitled",
            tourSessionId: payload.sessionId,
            reason: payload.reason,
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.questionChoiceSelected", ({ context, payload: { choiceId } }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "tour_question_choice_selected",
          properties: {
            ...getCommonPlaybackEventProperties(context),
            choiceId: choiceId,
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("avatar.skipClicked", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "step_skip_clicked",
          properties: {
            ...getCommonPlaybackEventProperties(context),
            avatarStatus: context.avatarStatus,
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("avatar.playClicked", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "play_clicked",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("avatar.pauseClicked", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "pause_clicked",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("avatar.replayClicked", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "step_replay_clicked",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("avatar.captionClicked", ({ payload: { status }, context }) => {
      invariant(context.guide, "Guide is not defined");
      if (status) {
        safeTrack(
          {
            event: "avatar_captions_enabled",
            properties: {
              ...getCommonPlaybackEventProperties(context),
            },
          },
          getUser?.()
        );
        return;
      }

      safeTrack(
        {
          event: "avatar_captions_disabled",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("avatar.muteClicked", ({ payload: { status }, context }) => {
      invariant(context.guide, "Guide is not defined");
      if (status) {
        safeTrack(
          {
            event: "avatar_muted",
            properties: {
              ...getCommonPlaybackEventProperties(context),
            },
          },
          getUser?.()
        );
        return;
      }

      safeTrack(
        {
          event: "avatar_unmuted",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("scheduler.userTargeted", ({ payload: { guide } }) => {
      safeTrack(
        {
          event: "user_targeted",
          properties: {
            guideId: guide.id,
            guideName: guide.title ?? "Untitled",
            tourPlaybackVersion: guide.tour_playback?.version ?? "unknown",
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourLifecycle.tourStarted", ({ payload: { guide } }) => {
      safeTrack(
        {
          event: "guide_displayed",
          properties: {
            guideId: guide.id,
            guideName: guide.title ?? "Untitled",
            tourPlaybackVersion: guide.tour_playback?.version ?? "unknown",
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.finished", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "guide_finished",
          properties: {
            ...getCommonPlaybackEventProperties(context),
            guideProgressPct: undefined,
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.dismissed", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "guide_dismissed",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.stepExecuted", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "guide_step_executed",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.targetElementNotFound", ({ payload: { reasons }, context }) => {
      invariant(context.guide, "Guide is not defined");
      console.log(
        "targetElementNotFound event received for step from analytics collector:",
        context.stepId,
        "reasons:",
        reasons
      );

      safeTrack(
        {
          event: "guide_step_target_not_found",
          properties: {
            ...getCommonPlaybackEventProperties(context),
            reason: reasons.join(", "),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.userWanderedOff", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "user_wandered_off",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.targetElementHighlighted", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "guide_target_element_highlighted",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.suspended", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "guide_suspended",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("avatar.minimizeClicked", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "avatar_minimized",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("avatar.mediaError", ({ context, payload }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "avatar_media_error",
          properties: {
            ...getCommonPlaybackEventProperties(context),
            details: JSON.stringify(payload),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.unsuspended", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "guide_unsuspended",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.introStartTourClicked", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "guide_intro_start_tour_clicked",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.introPostponeTourClicked", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "guide_intro_postpone_tour_clicked",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.introCloseTourClicked", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "guide_intro_close_tour_clicked",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.feedbackClicked", ({ context }) => {
      invariant(context.guide, "Guide is not defined");
      safeTrack(
        {
          event: "guide_feedback_clicked",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourLifecycle.checklistDisplayed", ({ payload: { checklist, completionState } }) => {
      safeTrack(
        {
          event: "checklist_displayed",
          properties: {
            checklistId: checklist.id,
            checklistTitle: checklist.title,
            checklistName: checklist.name,
            completionState,
            checklistVersion: checklist.version,
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourLifecycle.checklistExpandedFromAvatar", ({ payload: { checklist, completionState } }) => {
      safeTrack(
        {
          event: "checklist_expanded_from_avatar",
          properties: {
            checklistId: checklist.id,
            checklistTitle: checklist.title,
            checklistName: checklist.name,
            completionState,
            checklistVersion: checklist.version,
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourLifecycle.checklistExpanded", ({ payload: { checklist, completionState } }) => {
      safeTrack(
        {
          event: "checklist_expanded",
          properties: {
            checklistId: checklist.id,
            checklistTitle: checklist.title,
            checklistName: checklist.name,
            completionState,
            checklistVersion: checklist.version,
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourLifecycle.checklistMinimized", ({ payload: { checklist, completionState } }) => {
      safeTrack(
        {
          event: "checklist_minimized",
          properties: {
            checklistId: checklist.id,
            checklistTitle: checklist.title,
            checklistName: checklist.name,
            completionState,
            checklistVersion: checklist.version,
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourLifecycle.checklistDismissed", ({ payload: { checklist, completionState } }) => {
      safeTrack({
        event: "checklist_dismissed",
        properties: {
          checklistId: checklist.id,
          checklistTitle: checklist.title,
          checklistName: checklist.name,
          completionState,
          checklistVersion: checklist.version,
        },
      });
    });

    globalEventBus.on(
      "tourLifecycle.tourStartTriggeredFromChecklist",
      ({ payload: { checklist, guideId, completionState } }) => {
        safeTrack(
          {
            event: "checklist_tour_start_triggered",
            properties: {
              checklistId: checklist.id,
              checklistTitle: checklist.title,
              checklistName: checklist.name,
              guideId: guideId,
              completionState,
              checklistVersion: checklist.version,
            },
          },
          getUser?.()
        );
      }
    );

    globalEventBus.on("tourLifecycle.checklistCompleted", ({ payload: { checklist } }) => {
      safeTrack(
        {
          event: "checklist_completed",
          properties: {
            checklistId: checklist.id,
            checklistTitle: checklist.title,
            checklistName: checklist.name,
            checklistVersion: checklist.version,
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.shortClipFallbackTriggered", ({ context }) => {
      safeTrack(
        {
          event: "short_clip_fallback_triggered",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });

    globalEventBus.on("tourPlayback.shortClipProceedClicked", ({ context }) => {
      safeTrack(
        {
          event: "short_clip_proceed_clicked",
          properties: {
            ...getCommonPlaybackEventProperties(context),
          },
        },
        getUser?.()
      );
    });
  }
}

function getCommonPlaybackEventProperties(context: AllContexts) {
  invariant(context.guide, "Guide is not defined");
  return {
    guideId: context.guide.id,
    guideName: context.guide.title ?? "Untitled",
    stepId: context.stepId,
    stepType: context.stepType,
    stepIndex: context.stepIndex,
    sectionIndex: context.sectionIndex,
    stepOrder: context.stepOrder,
    totalSteps: context.totalSteps,
    totalSections: context.totalSections,
    totalSectionSteps: context.totalSectionSteps,
    flowKey: context.flowKey,
    time: Math.round(context.time),
    currentMediaDuration: Math.round(context.currentMediaDuration),
    tourPlaybackVersion: context.tourPlaybackVersion,
    guideProgressPct: context.tourProgress,
    tourSessionId: context.sessionId,
  };
}
